import type { AssetFlowOptions, BalanceExportOptions, BalancePageOptions, TransactionExportOptions, TransactionPageOptions, TransactionItem, TransactionCreateInput, TransactionUpdateInput } from '@/features/stock/domain/repositories/IStockRepository.js';
import type { StockAssetDto, StockBalanceDto, StockBatchDto, StockTransactionDto, StockTransactionHistoryPageDto, StockTransactionItemDto } from '@/features/stock/data/dtos/StockDto.js';
import { getSupabaseClient } from '@/core/supabase/client.js';
import { fetchAllPaged } from '@/core/supabase/pagination.js';

const ASSET_FIELDS = `id, org_id, project_id, available, name, sku, type, category, unit, pack, bundle, unit_per_bundle, unit_per_pack, default_unit_per_sold_item, unit_price, bundle_price, pack_price, description, tags, mfg_date, exp_date, created_by, created_at, updated_at`;
const BALANCE_FIELDS = `id, org_id, project_id, warehouse_id, asset_id, unit_qty, booked_unit_qty, inbound_unit_qty, outbound_unit_qty, shrinkage_unit_qty, updated_at, warehouse_location:locations!stock_balances_warehouse_id_fkey(id, name, province_code, full_address_cache)`;
const TX_FIELDS = `id, org_id, project_id, type, status, created_by_user_id, confirmed_by_user_id, confirmed_at, adjustment_direction, related_transaction_id, reject_reason, dispatched_at, rejected_at, from_warehouse_id, to_warehouse_id, notes, metadata, photos, created_at, updated_at`;
const BATCH_METADATA_FIELDS = `batch_code_id, batch_metadata:stock_batch_codes(id, batch_code, mfg_date, exp_date)`;
const TX_ITEM_FIELDS = `id, transaction_id, org_id, project_id, asset_id, source_batch_id, unit_qty, confirmed_unit_qty, shrinkage_unit_qty, shrinkage_reason, created_at, updated_at, batch_code_link:stock_transaction_batch_codes(${BATCH_METADATA_FIELDS})`;
const BATCH_FIELDS = `id, org_id, project_id, asset_id, location_id, origin_id, origin_tx_id, qty, booked_qty, received_qty, received_at, created_at, ${BATCH_METADATA_FIELDS}`;

function transactionItemPayload(item: TransactionItem) {
  return {
    ...(item.id !== undefined ? { id: item.id } : {}),
    asset_id: item.assetId,
    unit_qty: item.unitQty,
    source_batch_id: item.sourceBatchId ?? null,
    batch_code_id: item.batchCodeId ?? null,
    ...(item.batchCode !== undefined ? { batch_code: item.batchCode } : {}),
    ...(item.mfgDate !== undefined ? { mfg_date: item.mfgDate } : {}),
    ...(item.expDate !== undefined ? { exp_date: item.expDate } : {}),
  };
}

export class StockRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async listAssets(options: { orgId: string; projectId: string; search?: string }): Promise<StockAssetDto[]> {
    const pageSize = 1000;
    const rows: StockAssetDto[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await this.supabase.rpc('list_stock_assets_with_balance', {
        p_org_id: options.orgId,
        p_project_id: options.projectId,
        search_query: options.search ?? '',
        row_limit: pageSize,
        row_offset: offset,
      });
      if (error) throw new Error(error.message);

      const page = (data ?? []) as StockAssetDto[];
      rows.push(...page);
      if (page.length < pageSize) break;
      offset += page.length;
    }

    return rows;
  }

  async getAssetById(id: string): Promise<StockAssetDto | null> {
    const { data, error } = await this.supabase.from('stock_assets').select(ASSET_FIELDS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async createAsset(payload: Partial<StockAssetDto> & { org_id: string; project_id: string; name: string; sku: string }): Promise<StockAssetDto | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data, error } = await this.supabase.from('stock_assets')
      .insert({ ...payload, created_by: user?.id })
      .select(ASSET_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async updateAsset(id: string, payload: Partial<StockAssetDto>): Promise<StockAssetDto | null> {
    const { data, error } = await this.supabase.from('stock_assets').update(payload).eq('id', id).select(ASSET_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async deleteAsset(id: string): Promise<void> {
    const { error } = await this.supabase.from('stock_assets').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async listBalances(options: { orgId: string; projectId: string; warehouseId?: string }): Promise<StockBalanceDto[]> {
    const rows = await fetchAllPaged<StockBalanceDto>((from, to) => this.supabase.rpc('fn_stock_balance_rows', {
      p_org_id: options.orgId, p_project_id: options.projectId,
    }).range(from, to));
    return options.warehouseId ? rows.filter((b) => b.warehouse_id === options.warehouseId) : rows;
  }

  // Live batches (qty minus booked > 0) sitting at one location, for the
  // outflow batch picker. Provenance label is derived client-side from
  // origin_id — no join needed.
  async listBatchesAtWarehouse(options: { orgId: string; projectId: string; warehouseId: string }): Promise<StockBatchDto[]> {
    const batches = await fetchAllPaged<StockBatchDto>((from, to) =>
      this.supabase.from('stock_batches').select(BATCH_FIELDS)
        .eq('org_id', options.orgId).eq('project_id', options.projectId)
        .eq('location_id', options.warehouseId)
        .order('received_at', { ascending: true }).order('id')
        .range(from, to),
    );
    return batches;
  }

  // All batches of an asset across the project, for the provenance view.
  async listBatchesByAsset(options: { orgId: string; projectId: string; assetId: string }): Promise<StockBatchDto[]> {
    return fetchAllPaged<StockBatchDto>((from, to) =>
      this.supabase.from('stock_batches').select(BATCH_FIELDS)
        .eq('org_id', options.orgId).eq('project_id', options.projectId)
        .eq('asset_id', options.assetId)
        .order('received_at', { ascending: true }).order('id')
        .range(from, to),
    );
  }

  async listAssetFlow(options: AssetFlowOptions): Promise<StockTransactionDto[]> {
    const { data, error } = await this.supabase.rpc('fn_stock_asset_flow_related', {
      p_org_id: options.orgId,
      p_project_id: options.projectId,
      p_asset_id: options.assetId,
      p_warehouse_id: options.filter.warehouse,
      p_filter: options.filter,
    });
    if (error) throw new Error(error.message);
    return data;
  }
  async listBalancePage(options: BalancePageOptions) {
    const {data,error} = await this.supabase.rpc('fn_stock_balance_page', {p_org_id:options.orgId,p_project_id:options.projectId,
      p_filter:{warehouse:options.warehouse,asset:options.asset,state:options.state,group:options.group},p_limit:options.limit ?? 20,p_offset:options.offset ?? 0});
    if(error) throw new Error(error.message);
    return data as {items: StockBalanceDto[]; total: number; asOf: string; summary: {assets:number;warehouses:number;zero:number;anomaly:number;quantity:number|null}};
  }
  async listBalanceExport(options: BalanceExportOptions): Promise<StockBalanceDto[]> {
    const { data, error } = await this.supabase.rpc('fn_stock_balance_export', {
      p_org_id: options.orgId,
      p_project_id: options.projectId,
      p_filter: { warehouse: options.warehouse, asset: options.asset, state: options.state, group: options.group },
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as StockBalanceDto[];
  }
  async getTransaction(id: string): Promise<StockTransactionDto | null> {
    const { data, error } = await this.supabase.rpc('fn_stock_transaction_detail', { p_id: id });
    if (error) throw new Error(error.message);
    return data;
  }
  async listTransactionPage(options: TransactionPageOptions) {
    const { data, error } = await this.supabase.rpc('fn_stock_transaction_page', {
      p_org_id: options.orgId, p_project_id: options.projectId, p_filter: options.filter ?? {},
      p_limit: options.limit ?? 20, p_offset: options.offset ?? 0,
    });
    if (error) throw new Error(error.message);
    return data as { items: StockTransactionDto[]; total: number; asOf: string; summary: {total: number; pending: number; delivering: number; aging: number; shortage: number; problem: number} };
  }
  async listTransactionExport(options: TransactionExportOptions): Promise<StockTransactionDto[]> {
    const { data, error } = await this.supabase.rpc('fn_stock_transaction_export', {
      p_org_id: options.orgId, p_project_id: options.projectId, p_filter: options.filter ?? {},
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as StockTransactionDto[];
  }
  async listTransactions(options: { orgId: string; projectId: string; type?: string; status?: string; assetId?: string }): Promise<StockTransactionDto[]> {
    return fetchAllPaged<StockTransactionDto>((from, to) => {
      let query = this.supabase.from('stock_transactions')
        .select(`${TX_FIELDS}, stock_transaction_items${options.assetId ? '!inner' : ''}(${TX_ITEM_FIELDS}), from_location:locations!stock_transactions_from_warehouse_id_fkey(id,name), to_location:locations!stock_transactions_to_warehouse_id_fkey(id,name)`)
        .eq('org_id', options.orgId).eq('project_id', options.projectId);
      if (options.type) query = query.eq('type', options.type);
      if (options.status) query = query.eq('status', options.status);
      if (options.assetId) query = query.eq('stock_transaction_items.asset_id', options.assetId);
      return query.order('created_at', { ascending: false }).order('id', { ascending: false }).range(from, to);
    });
  }

  async createTransaction(input: TransactionCreateInput): Promise<StockTransactionDto | null> {
    const { data, error } = await this.supabase.rpc('fn_create_stock_transaction', {
      p_org_id: input.orgId, p_project_id: input.projectId, p_type: input.type, p_status: input.status ?? 'pending',
      p_from_warehouse_id: input.fromWarehouseId ?? null, p_to_warehouse_id: input.toWarehouseId ?? null,
      p_notes: input.notes ?? null, p_photos: [],
      p_metadata: { adjustment_direction: input.adjustmentDirection ?? 'increase', related_transaction_id: input.relatedTransactionId ?? null },
      p_items: input.items.map(transactionItemPayload),
    });
    if (error) throw new Error(error.message);
    return data;
  }
  async updateTransaction(id: string, input: TransactionUpdateInput): Promise<StockTransactionDto | null> {
    const { data, error } = await this.supabase.rpc('fn_update_stock_transaction', {
      p_id: id, p_type: input.type, p_status: input.status,
      p_from_warehouse_id: input.fromWarehouseId ?? null, p_to_warehouse_id: input.toWarehouseId ?? null,
      p_notes: input.notes ?? null,
      p_items: (input.items ?? []).map(transactionItemPayload),
    });
    if (error) throw new Error(error.message);
    return data;
  }
  async transitionTransaction(id: string, status: string, reason?: string, photos: string[] = []): Promise<StockTransactionDto | null> {
    const { data, error } = await this.supabase.rpc('fn_transition_stock_transaction', {
      p_id: id, p_status: status, p_reason: reason ?? null, p_photos: photos,
    });
    if (error) throw new Error(error.message);
    return data;
  }

  async deleteTransaction(id: string): Promise<void> {
    const { error } = await this.supabase.rpc('fn_delete_stock_transaction', {
      p_transaction_id: id,
    });
    if (error) throw new Error(error.message);
  }

  async acceptTransaction(
    transactionId: string,
    confirmedItems: Array<{
      id?: string;
      asset_id: string;
      source_batch_id?: string | null;
      batch_code_id?: string | null;
      unit_qty: number;
      confirmed_unit_qty: number;
      shrinkage_unit_qty: number;
      shrinkage_reason?: string | null;
    }>,
    photos: string[] = [],
  ): Promise<StockTransactionDto | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data, error } = await this.supabase.rpc('fn_accept_stock_transaction', {
      p_transaction_id: transactionId,
      p_confirmed_items: confirmedItems,
      p_user_id: user?.id,
      p_photos: photos,
    });
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async rejectTransaction(transactionId: string, reason: string, photos: string[] = []): Promise<StockTransactionDto | null> {
    return this.transitionTransaction(transactionId, 'rejected', reason, photos);
  }

  async recalcWarehouse(orgId: string, projectId: string, warehouseId: string): Promise<void> {
    const { error } = await this.supabase.rpc('fn_recalculate_stock_balance_for_warehouse', {
      p_org_id: orgId, p_project_id: projectId, p_warehouse_id: warehouseId,
    });
    if (error) throw new Error(error.message);
  }

  async recalcProject(orgId: string, projectId: string): Promise<void> {
    const { error } = await this.supabase.rpc('fn_recalculate_stock_balances', {
      p_org_id: orgId, p_project_id: projectId,
    });
    if (error) throw new Error(error.message);
  }

  async listAssetHistory(options: { orgId: string; projectId: string; assetId: string; warehouseId?: string }): Promise<StockTransactionDto[]> {
    // Every transaction carrying a line for this asset. `!inner` drops txs
    // without a matching item; the embedded filter keeps only this asset's
    // item nested on each tx. When warehouseId given, narrow to txs touching
    // that warehouse on either side; otherwise return the asset's whole life.
    return fetchAllPaged<StockTransactionDto>((from,to) => {
      let q = this.supabase.from('stock_transactions')
        .select(`${TX_FIELDS}, stock_transaction_items!inner(${TX_ITEM_FIELDS}), from_location:locations!stock_transactions_from_warehouse_id_fkey(id,name), to_location:locations!stock_transactions_to_warehouse_id_fkey(id,name)`)
        .eq('org_id',options.orgId).eq('project_id',options.projectId).eq('stock_transaction_items.asset_id',options.assetId);
      if (options.warehouseId) q = q.or(`from_warehouse_id.eq.${options.warehouseId},to_warehouse_id.eq.${options.warehouseId}`);
      return q.order('created_at',{ascending:false}).order('id',{ascending:false}).range(from,to);
    });
  }

  async listAssetHistoryPage(options: {
    orgId: string;
    projectId: string;
    assetId: string;
    warehouseId?: string;
    limit?: number;
    cursor?: { createdAt: string; id: string };
  }): Promise<StockTransactionHistoryPageDto> {
    const pageSize = Math.min(Math.max(options.limit ?? 50, 1), 100);
    let q = this.supabase.from('stock_transactions')
      .select(`${TX_FIELDS}, stock_transaction_items!inner(${TX_ITEM_FIELDS}), from_location:locations!stock_transactions_from_warehouse_id_fkey(id, name), to_location:locations!stock_transactions_to_warehouse_id_fkey(id, name)`)
      .eq('org_id', options.orgId).eq('project_id', options.projectId)
      .eq('stock_transaction_items.asset_id', options.assetId);

    if (options.cursor) {
      const { createdAt, id } = options.cursor;
      if (options.warehouseId) {
        // Distribute (warehouse filter) AND (cursor filter) into DNF because
        // PostgREST exposes each top-level OR as a separate filter.
        q = q.or([
          `and(from_warehouse_id.eq.${options.warehouseId},created_at.lt.${createdAt})`,
          `and(from_warehouse_id.eq.${options.warehouseId},created_at.eq.${createdAt},id.lt.${id})`,
          `and(to_warehouse_id.eq.${options.warehouseId},created_at.lt.${createdAt})`,
          `and(to_warehouse_id.eq.${options.warehouseId},created_at.eq.${createdAt},id.lt.${id})`,
        ].join(','));
      } else {
        q = q.or(`created_at.lt.${createdAt},and(created_at.eq.${createdAt},id.lt.${id})`);
      }
    } else if (options.warehouseId) {
      q = q.or(`from_warehouse_id.eq.${options.warehouseId},to_warehouse_id.eq.${options.warehouseId}`);
    }

    const { data, error } = await q
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(pageSize + 1);
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as StockTransactionDto[];
    const hasNextPage = rows.length > pageSize;
    const items = hasNextPage ? rows.slice(0, pageSize) : rows;
    const last = items[items.length - 1];
    return {
      items,
      nextCursor: hasNextPage && last ? { created_at: last.created_at, id: last.id } : null,
    };
  }

  async listTransactionItems(transactionId: string): Promise<StockTransactionItemDto[]> {
    const { data, error } = await this.supabase.from('stock_transaction_items')
      .select(TX_ITEM_FIELDS).eq('transaction_id', transactionId).order('created_at').order('id');
    if (error) throw new Error(error.message);
    return data ?? [];
  }

}
